
import React, { useRef, useEffect } from 'react';

/**
 * Creates DOM element to be used as React root.
 * @param id - The id attribute to be set on the root element
 * @returns The created HTML element
 */
function createRootElement(id: string): HTMLDivElement {
    const rootContainer = document.createElement('div');
    rootContainer.setAttribute('id', id);
    return rootContainer;
}

/**
 * Appends element as last child of body.
 * @param rootElem - The root element to be appended
 */
function addRootElement(rootElem: HTMLElement): void {
    document.body.insertBefore(
        rootElem,
        document.body.lastElementChild?.nextElementSibling || null
    );
}

/**
 * Hook to create a React Portal.
 * Automatically handles creating and tearing-down the root elements (no SSR
 * makes this trivial), so there is no need to ensure the parent target already
 * exists.
 * 
 * @example
 * const target = usePortal(id);
 * return createPortal(children, target);
 * 
 * @param id - The id of the target container, e.g 'modal' or 'spotlight'
 * @returns The DOM node to use as the Portal target
 */
function usePortal(id: string): HTMLDivElement {
    const rootElemRef = useRef<HTMLDivElement | null>(null);

    useEffect(function setupElement() {
        // Look for existing target dom element to append to
        const existingParent = document.querySelector(`#${id}`) as HTMLElement;
        // Parent is either a new root or the existing dom element
        const parentElem = existingParent || createRootElement(id);

        // If there is no existing DOM element, add a new one.
        if (!existingParent) {
            addRootElement(parentElem);
        }

        // Add the detached element to the parent
        if (rootElemRef.current) {
            parentElem.appendChild(rootElemRef.current);
        }

        return function removeElement() {
            rootElemRef.current?.remove();
            if (parentElem.childElementCount === 0) {
                parentElem.remove();
            }
        };
    }, [id]);

    /**
     * It's important we evaluate this lazily:
     * - We need first render to contain the DOM element, so it shouldn't happen
     *   in useEffect. We would normally put this in the constructor().
     * - We can't do 'const rootElemRef = useRef(document.createElement('div))',
     *   since this will run every single render (that's a lot).
     * - We want the ref to consistently point to the same DOM element and only
     *   ever run once.
     */
    function getRootElem(): HTMLDivElement {
        if (!rootElemRef.current) {
            rootElemRef.current = document.createElement('div');
        }
        return rootElemRef.current;
    }

    return getRootElem();
}

export default usePortal;